import Backbone from 'backbone';
import Promise from 'bluebird';
import _ from 'underscore';
import $ from 'jquery';

import { StepsWizardBuilder } from '@bingads-webui/steps-wizard';

import { CommonImportRepository } from '@bingads-webui-campaign/import-shared-repositories';
import { ContinueImportView } from './continue-import/index';
import template from './base-import-template.pug';

export class BaseImportView extends Backbone.View {
  initialize({
    deps,
    steps,
    initialState = {},
    options,
    onSwitchFinished,
  }) {
    this.checkRequiredDeps(deps);

    this.deps = deps;
    this.steps = steps;
    this.initialState = initialState;
    this.options = options;
    // by default, the other steps except last step will be hidden when wizard finish.
    // param: wizard
    this.showAllStepsInLastStepFunc = this.options.showAllStepsInLastStepFunc;
    if (!this.showAllStepsInLastStepFunc) {
      this.showAllStepsInLastStepFunc = () => false;
    }
    this.onSwitchFinished = onSwitchFinished;
    this.repository = new CommonImportRepository({
      deps: this.deps,
    });
    this.logTrace = this.repository.logTrace;
  }

  checkRequiredDeps(deps) {
    deps.check('adGroupMedium');
    deps.check('config');
    deps.check('i18n');
    deps.check('i18nAmdLoader');
    deps.check('i18nErrors');
    deps.check('language');
    deps.check('market');
    deps.check('odata');
    deps.check('timeZoneEnumMapper');
    deps.check('showPayment');
    deps.check('paymentMethodUrl');
    deps.check('schema');
    deps.check('storeList');
    deps.check('alertCenter');
    deps.check('logActivities');
    deps.check('defaultLogActivity');
    deps.check('sharedCookieDomain');
    deps.check('statusOrderToBlockerTitleMap');
    deps.check('statusKeyToOrderMap');
  }

  checkIfNeedDisablePage() {
    // for read only user or inactive account, disable buttons to avoid operations.
    if (this.deps.config.get('Permissions').IsReadOnlyUser
      || this.deps.config.get('CurrentAccountInActive')) {
      this.$('button').attr('disabled', 'disabled');
      this.$('input[type=button]').attr('disabled', 'disabled');
    }
  }

  render() {
    if (this.initialState.importId) {
      this.initializeStepsWizard();
      this.checkIfNeedDisablePage();
      return Promise.resolve(this);
    }
    return this.renderInitialLoad().then(() => {
      this.checkIfNeedDisablePage();
      return this;
    });
  }

  remove() {
    // set remove flag to avoid extra handling
    if (this.deps.importSource) {
      // set remove flag in schedule import step for google import and facebook import
      const scheduleImportStepView = _.findWhere(this.steps, { stepName: 'schedule-import-step' }).stepView;
      if (scheduleImportStepView) {
        scheduleImportStepView.stepRemoved = true;
      }
    } else {
      // set remove flag in import options step for file import
      const importOptionsStepView = _.findWhere(this.steps, { stepName: 'import-options-step' }).stepView;
      if (importOptionsStepView) {
        importOptionsStepView.stepRemoved = true;
      }
    }

    // close the blocker if any
    if (this.currentStepInstance.repository.blockerView) {
      this.currentStepInstance.repository.blockerView.closeBlock();
    }

    // should stop send polling status api
    this.currentStepInstance.repository.stopPollingStatus();

    super.remove();
  }

  goToNextStep(importStatus = '') {
    if (!this.onContinueImportView) {
      this.view.currentStepInstance.patchState({
        importStatus,
      });
      this.view.currentStepInstance.wizardShouldGoNext()
        .then((result) => {
          if (result) {
            this.view.goTo(this.view.nextStepName);
          }

          return this;
        });
    } else {
      throw new Error('Should not be trying to go to next step while on Continue Import view');
    }
  }

  goToStep(stepName) {
    this.view.goTo(stepName);
  }

  get currentStepInstance() {
    return this.view.currentStepInstance;
  }

  renderInitialLoad() {
    let statusRepository;
    if (this.deps.isMultiAccountImport) {
      statusRepository = Promise.resolve();
    } else if (this.deps.isForVNext) {
      statusRepository = Promise.resolve(this.deps.importStatus);
    } else {
      statusRepository = this.repository.isAnyImportPending();
    }

    return statusRepository
      .then((status) => {
        if (status && !this.options.isSignUpMode) {
          const options = _.extend(
            {},
            _.pick(status, 'importType', 'importId', 'importNotSubmitted'), this.options.continueImportOptions
          );

          this.view = new ContinueImportView({
            deps: _.extend(this.deps, {
              repository: this.repository,
            }),
            options,
          });
          this.onContinueImportView = true;
          this.$el.html(this.view.render().el);

          this.logTrace('continue import will be shown');
        } else {
          this.onContinueImportView = false;
          this.initializeStepsWizard();
        }

        return this;
      })
      .catch((err) => {
        this.repository.logException(err);
        this.deps.alertCenter.showError(this.repository.getLocalizedErrorMessage());
      });
  }

  initializeStepsWizard() {
    let wizard = new StepsWizardBuilder();

    _.each(this.steps, ({
      stepName,
      stepView,
    }) => {
      wizard.withStep(stepName, stepView);
    });

    // not show edit for continue import and sign up import
    const isEditEnabledForWizard = !this.initialState.importId && this.options.enableEdit;

    wizard = wizard.startsFrom(this.options.currentStep)
      .setEvents({
        'click .step-header-link': (e) => {
          this.trigger('click:step.header.link', e);
        },
      })
      .setInitialState(_.defaults({ isEditEnabledForWizard }, this.initialState));

    if (isEditEnabledForWizard) {
      wizard = wizard.withEditButton();
    }

    this.view = wizard.build();
    // The $lastVisist state in step wizard is messed up in A=>B=>C, edit B, edit A scenario.
    // Workaround fix is to use our customized logic.
    let lastVisitStepName;

    _.each(this.steps, ({ stepView }) => {
      stepView.on('next', () => {
        const { nextStepName } = this.view;
        lastVisitStepName = nextStepName;
        this.logTrace(`will go to ${nextStepName}`);
        this.view.goTo(nextStepName);
      });
      stepView.on('edit:false', () => {
        this.logTrace(`no edit, will go to ${lastVisitStepName}`);
        this.view.goTo(lastVisitStepName);
      });
      stepView.on('import:error', () => {
        this.logTrace('has error, hide edit icon');
        this.$('.edit-step').hide();
      });
    });
    // if we change dom on switch:end, we may break the render of import summary grid.
    this.view.on('switch:begin', ({ destStepName }) => {
      if (isEditEnabledForWizard) {
        this.view.currentStepInstance.patchState({
          [`${this.view.currentStepName}.isEdited`]: this.view.currentStepInstance.isEditedOrFirstSubmit,
        });
      }

      if (destStepName === this.view.lastStep.name) {
        if (!this.showAllStepsInLastStepFunc(this.view)) {
          const otherSteps = _.initial(this.steps);

          _.each(otherSteps, (item) => {
            item.stepView.remove();
            $(`[data-step-name='${item.stepName}']`).closest('.step-container').hide();
          });
        }

        if (this.options.isSignUpMode) {
          $('[data-step-name="import-summary-step"]').closest('.step-container').show();
          $('.import-page-title').hide();
        }
      }
    });

    this.view.on('switch:finish', ({ stepName }) => {
      if (this.onSwitchFinished) {
        this.onSwitchFinished();
      }
      if (stepName === this.view.lastStep.name) {
        this.$('.edit-step').hide(); // hide edit icon when in last step
      }
    });

    this.$el.html(template({
      pageTitle: this.options.pageTitle,
      subTitle: this.options.subTitle,
    }));
    this.$('.import-steps').html(this.view.render().el);
  }
}
